import React from "react";
import { Loader } from "smart-react";
import { PencilLine } from "lucide-react";
import { ListViewItemRender } from "./../ListView/ListView";
import FeatureDetail from "../Components/ContentSlider/FeatureDetail";
import {
  isButtonEnabled,
  showSubscribedLabel,
  showExpiredLabel,
  showFeatureExpirationDate,
} from "../Utils/Util";
import ProductListHOC from "../ProductListHOC";
import {
  ProductAccordionItem,
  ProductButton,
  ProductEmptyState,
  ProductIconBadge,
  ProductMetaDot,
  ProductSummaryStrip,
  StatusTag,
} from "../Components/ProductListPrimitives";
import {
  getPortfolioMetrics,
  getProductMetrics,
} from "../Utils/ProductMetrics";

const AdminProductList = ({
  tenantID,
  isOrganizationProduct,
  subscribed = true,
  products,
  loader,
  expanded,
  setExpanded,
  updateData,
  isFeaturesEnabled,
  isAllProductFeaturesSubscribed,
  isAllProductFeaturesExpired,
  isAnyProductFeatureExpired,
  isOrganizationAdminRole,
  isWorldTenant,
  hasFeaturePermissions,
  user,
}) => {
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [selectedFeature, setSelectedFeature] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);

  const openProductEditor = (product) => {
    setSelectedFeature(null);
    setSelectedProduct(product);
    setShowModal(true);
  };

  const openFeatureEditor = (feature) => {
    setSelectedProduct(null);
    setSelectedFeature(feature);
    setShowModal(true);
  };

  React.useEffect(() => {
    if (!showModal) {
      setSelectedProduct(null);
      setSelectedFeature(null);
    }
  }, [showModal]);

  const isEditFeatureButton = ({ feature, featurePermission }) =>
    feature?.Permissions?.length > 0 && featurePermission?.SubscriptionEndDate;

  const toggleProduct = (productId) => {
    setExpanded(expanded === productId ? "" : productId);
  };

  const visibleProducts = React.useMemo(
    () =>
      (products ?? []).filter(
        (product) =>
          (subscribed && hasFeaturePermissions(product?.features)) ||
          !subscribed,
      ),
    [hasFeaturePermissions, products, subscribed],
  );

  const summary = React.useMemo(
    () => getPortfolioMetrics(visibleProducts),
    [visibleProducts],
  );

  const summaryItems = [
    {
      label: "Products",
      value: summary.productsCount,
      hint: "Managed products",
    },
    {
      label: "Active Modules",
      value: summary.activeCount,
      hint: `${summary.moduleCount} total modules`,
    },
    {
      label: "Expiring Soon",
      value: summary.expiringSoonCount,
      hint: "Needs attention",
    },
  ];

  return (
    <>
      {loader && <Loader />}

      <div className="flex flex-col px-5 pb-5">
        {visibleProducts.length > 0 ? (
          <>
            <ProductSummaryStrip items={summaryItems} className="mb-1" />

            {visibleProducts.map((product) =>
              (() => {
                const metrics = getProductMetrics(product);
                const canSubscribeAll =
                  !isFeaturesEnabled(product) ||
                  (isOrganizationProduct && !isOrganizationAdminRole(user)) ||
                  isAllProductFeaturesSubscribed(product) ||
                  isWorldTenant(tenantID) ||
                  subscribed
                    ? false
                    : true;

                return (
                  <ProductAccordionItem
                    key={product.product_id}
                    open={expanded === product.product_id}
                    onToggle={() => toggleProduct(product.product_id)}
                    title={
                      <div className="flex min-w-0 items-center gap-3">
                        <ProductIconBadge productName={product.display_name} />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2.5">
                            <span className="truncate text-[16px] font-semibold tracking-[-0.02em] text-foreground sm:text-[17px]">
                              {product.display_name}
                            </span>
                            <button
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-primary transition-colors hover:bg-primary/10"
                              onClick={(event) => {
                                event.stopPropagation();
                                openProductEditor(product);
                              }}
                              aria-label={`Edit ${product.display_name}`}
                            >
                              <PencilLine className="h-4 w-4" />
                            </button>
                            {isAllProductFeaturesExpired(product?.features) && (
                              <StatusTag tone="error">Expired</StatusTag>
                            )}
                            {isAllProductFeaturesSubscribed(product) &&
                              !isAnyProductFeatureExpired(
                                product?.features,
                              ) && (
                                <StatusTag tone="success">Subscribed</StatusTag>
                              )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[13px] font-medium text-muted-foreground">
                            <span>{metrics.moduleCount} Modules</span>
                            <ProductMetaDot />
                            <span>{metrics.activeCount} Active</span>
                            {metrics.expiringSoonCount > 0 ? (
                              <>
                                <ProductMetaDot />
                                <span>
                                  {metrics.expiringSoonCount} Expiring Soon
                                </span>
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    }
                    action={
                      <div className="flex items-center gap-2">
                        <ProductButton
                          variant="ghost"
                          onClick={(event) => {
                            event.stopPropagation();
                            openProductEditor(product);
                          }}
                        >
                          Manage
                        </ProductButton>
                        {canSubscribeAll ? (
                          <ProductButton
                            onClick={() => openProductEditor(product)}
                          >
                            Subscribe
                          </ProductButton>
                        ) : null}
                      </div>
                    }
                  >
                    <div className="flex flex-col">
                      {product?.features?.map((feature) => (
                        <ListViewItemRender
                          key={feature.feature_id}
                          dataItem={feature}
                          isOrganizationProduct={isOrganizationProduct}
                          isOrganizationAdmin={isOrganizationAdminRole(user)}
                          subscribed={subscribed}
                          tenantID={tenantID}
                          isButtonEnabled={isButtonEnabled}
                          showSubscribedLabel={showSubscribedLabel}
                          showExpiredLabel={showExpiredLabel}
                          showFeatureExpirationDate={showFeatureExpirationDate}
                          showButtonTitle={() => false}
                          subscribeButtonClick={openFeatureEditor}
                          isEditFeatureButton={isEditFeatureButton}
                        />
                      ))}
                    </div>
                  </ProductAccordionItem>
                );
              })(),
            )}
          </>
        ) : !loader ? (
          <ProductEmptyState
            title={
              subscribed ? "No subscribed products yet" : "No products found"
            }
            description={
              subscribed
                ? "Subscribed products will appear here once organization access is configured."
                : "There are no products available to manage right now."
            }
          />
        ) : null}
      </div>

      <FeatureDetail
        open={showModal}
        setShowModal={setShowModal}
        selectedProduct={selectedProduct}
        selectedFeature={selectedFeature}
        updateData={updateData}
        tenantID={tenantID}
      />
    </>
  );
};

export default ProductListHOC(AdminProductList);
